// v1.22.0 · Auditoría · Plan de cuentas de la empresa: saldos, banderas y aclaraciones del plan (naturaleza, cuenta correctora, nota)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Flag, Pencil } from 'lucide-react';
import { C, SEV, api, errMsg, money } from './api';
import { SevTag } from './Panel';

export default function Plan({ cid, onOpenFlag, onChanged, goTab }) {
  const [data, setData] = useState({ items: [], has_plan: false });
  const [q, setQ] = useState('');
  const [onlyFlags, setOnlyFlags] = useState(false);
  const [maxLevel, setMaxLevel] = useState(9);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => { try { setData(await api.get(`/companies/${cid}/accounts`)); } catch (e) { toast.error(errMsg(e)); } }, [cid]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const flagged = new Set();
    data.items.forEach((a) => { if (a.flags?.length) a.code.split('.').forEach((_, i, arr) => flagged.add(arr.slice(0, i + 1).join('.'))); });
    return data.items.filter((a) => a.level <= maxLevel && (!onlyFlags || flagged.has(a.code))
      && (!q || `${a.code} ${a.name}`.toLowerCase().includes(q.toLowerCase())));
  }, [data, q, onlyFlags, maxLevel]);
  const nFlag = data.items.filter((a) => a.flags?.length).length;
  const nNotes = data.items.filter((a) => a.notes || a.nature || a.contra === true).length;

  return (
    <div className="neu-grid" style={{ gap: 18 }}>
      <section className="neu-card">
        <p className="neu-title" style={{ fontSize: 15 }}>Plan de cuentas {data.has_plan ? '' : '(armado con los estados y el mayor: suba el plan oficial)'}</p>
        <p className="neu-sub">{data.items.length} cuentas · {nFlag} con banderas · {nNotes} con aclaraciones del plan.
          Aclare aquí la naturaleza de una cuenta, si es correctora (depreciación, provisión) o para qué se usa: el análisis se recalcula con eso.</p>
        <div className="neu-row" style={{ marginTop: 12 }}>
          <input className="neu-input" style={{ maxWidth: 260 }} placeholder="Buscar código o nombre" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="neu-select" style={{ width: 'auto' }} value={maxLevel} onChange={(e) => setMaxLevel(+e.target.value)} aria-label="Nivel">
            {[1, 2, 3, 4, 5, 9].map((n) => <option key={n} value={n}>{n === 9 ? 'Todos los niveles' : `Hasta nivel ${n}`}</option>)}</select>
          <button type="button" className={`neu-btn neu-btn-sm ${onlyFlags ? 'is-on' : ''}`} onClick={() => setOnlyFlags(!onlyFlags)}><Flag size={13} /> Solo con banderas</button>
          {!data.has_plan && <button type="button" className="neu-btn neu-btn-sm neu-btn-primary" onClick={() => goTab('expediente')}>Subir plan de cuentas</button>}
        </div>
      </section>
      <section className="neu-card" style={{ padding: 10 }}>
        <div className="neu-scroll" style={{ maxHeight: 640 }}>
          <table className="neu-table">
            <thead><tr><th>Código</th><th>Cuenta</th><th className="num">Saldo al corte</th><th>Banderas</th><th>Aclaración del plan</th><th /></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.code}>
                  <td className="tabular" style={{ whiteSpace: 'nowrap', paddingLeft: 10 + (a.level - 1) * 12, fontWeight: a.level <= 2 ? 800 : 500 }}>{a.code}</td>
                  <td style={{ fontWeight: a.level <= 2 ? 700 : 400 }}>{a.name}{a.duplicate && <span className="neu-chip neu-chip-red" style={{ marginLeft: 6 }}>código repetido</span>}</td>
                  <td className="num" style={{ color: a.amount < 0 ? SEV.alta.color : C.ink }}>{a.amount === null || a.amount === undefined ? '' : money(a.amount)}</td>
                  <td>{(a.flags || []).slice(0, 3).map((f) => <button type="button" key={f.key} className="neu-btn neu-btn-sm" style={{ margin: 2 }} title={f.title} onClick={() => onOpenFlag(f.key)}><SevTag s={f.severity} /></button>)}
                    {(a.flags || []).length > 3 && <span className="neu-hint"> +{a.flags.length - 3}</span>}</td>
                  <td className="neu-hint" style={{ maxWidth: 280 }}>{[a.nature && `Naturaleza ${a.nature === 'D' ? 'deudora' : 'acreedora'}`, a.contra && 'Cuenta correctora', a.notes].filter(Boolean).join(' · ')}</td>
                  <td>{data.has_plan && <button type="button" className="neu-btn neu-icon-btn" aria-label={`Aclarar ${a.code}`} onClick={() => setEdit({ code: a.code, name: a.name, nature: a.nature || '', contra: !!a.contra, notes: a.notes || '' })}><Pencil size={13} /></button>}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="neu-empty">Sin cuentas con este filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      {edit && (
        <div className="neu-modal-bg" role="dialog" aria-modal="true" aria-label="Aclarar cuenta" onClick={(e) => e.target === e.currentTarget && setEdit(null)}>
          <div className="neu-modal">
            <p className="neu-label">Aclarar cuenta del plan</p>
            <p className="neu-title" style={{ fontSize: 16 }}>{edit.code} {edit.name}</p>
            <div className="neu-grid" style={{ gap: 12, marginTop: 12 }}>
              <label className="neu-field"><span className="neu-label">Naturaleza</span>
                <select className="neu-select" value={edit.nature} onChange={(e) => setEdit({ ...edit, nature: e.target.value })}>
                  <option value="">Según su clase (automática)</option><option value="D">Deudora</option><option value="C">Acreedora</option></select></label>
              <label className="neu-row" style={{ fontSize: 13 }}><input type="checkbox" checked={edit.contra} onChange={(e) => setEdit({ ...edit, contra: e.target.checked })} />
                Es cuenta correctora (depreciación, provisión, descuentos…)</label>
              <label className="neu-field"><span className="neu-label">Uso de la cuenta / nota para la auditoría</span>
                <textarea className="neu-textarea" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                          placeholder="Ej.: cuenta puente para reposiciones de caja chica; debe quedar en cero al cierre de cada mes." /></label>
            </div>
            <div className="neu-row" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
              <button type="button" className="neu-btn" onClick={() => setEdit(null)}>Cancelar</button>
              <button type="button" className="neu-btn neu-btn-primary" onClick={async () => {
                try { await api.patch(`/companies/${cid}/accounts/${encodeURIComponent(edit.code)}`, { nature: edit.nature, contra: edit.contra, notes: edit.notes });
                  toast.success('Aclaración guardada; análisis recalculado'); setEdit(null); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
              }}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
